import React from 'react'

import { Check, X } from 'lucide-react'
import { Controller, UseFormReturn } from 'react-hook-form'

import { useTranslation } from '@/core/i18n/useTranslation'
import { Button } from '@/shared/components/atoms/Button'
import { ErrorMessage } from '@/shared/components/atoms/ErrorMessage'
import { Label } from '@/shared/components/atoms/Label/Label'
import { PasswordInput } from '@/shared/components/atoms/PasswordInput'

import { AuthLayout } from '../AuthLayout'

interface ResetPasswordFormData {
	password: string
	password_confirmation: string
}

interface ResetPasswordPageUIProps {
	form: UseFormReturn<ResetPasswordFormData>
	isLoading: boolean
	error: string | null
	onFinish: (values: ResetPasswordFormData) => void
}

export const ResetPasswordPageUI: React.FC<ResetPasswordPageUIProps> = ({
	form,
	isLoading,
	error,
	onFinish,
}) => {
	const { t } = useTranslation('auth')

	const validatePasswordRequirements = (password: string) => {
		return {
			minLength: password.length >= 8,
			hasNumber: /\d/.test(password),
			hasSpecialChar: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password),
		}
	}

	const currentPassword = form.watch('password') || ''
	const passwordConfirmation = form.watch('password_confirmation') || ''
	const passwordRequirements = validatePasswordRequirements(currentPassword)

	const allPasswordRequirementsMet =
		passwordRequirements.minLength &&
		passwordRequirements.hasNumber &&
		passwordRequirements.hasSpecialChar

	const isFormValid =
		currentPassword.length > 0 &&
		passwordConfirmation.length > 0 &&
		allPasswordRequirementsMet &&
		Object.keys(form.formState.errors).length === 0

	const handleFormSubmit = form.handleSubmit(onFinish)

	return (
		<AuthLayout>
			<div className="space-y-4">
				<h1 className="text-4xl font-bold mb-0">
					{t('resetPassword.title', { defaultValue: 'Reset your password' })}
				</h1>

				<p className="mb-4">
					{t('resetPassword.instruction', {
						defaultValue: 'Please enter a new password for your account.',
					})}
				</p>

				{error && (
					<div className="p-3 rounded-md bg-red-50 border border-red-200">
						<p className="text-sm text-red-600">{error}</p>
					</div>
				)}

				<form onSubmit={handleFormSubmit} className="space-y-4">
					<div>
						<Label className="text-xs mb-2 block">
							{t('register.password')}
						</Label>
						<Controller
							control={form.control}
							name="password"
							render={({ field, fieldState: { error } }) => (
								<div>
									<PasswordInput
										placeholder={t('register.password')}
										className="h-10"
										{...field}
									/>
									{error && <ErrorMessage error={error.message ?? ''} />}
								</div>
							)}
						/>
					</div>

					<div>
						<Label className="text-xs mb-2 block">
							{t('register.confirmPassword')}
						</Label>
						<Controller
							control={form.control}
							name="password_confirmation"
							render={({ field, fieldState: { error } }) => (
								<div>
									<PasswordInput
										placeholder={t('register.confirmPassword')}
										className="h-10"
										{...field}
									/>
									{error && <ErrorMessage error={error.message ?? ''} />}
								</div>
							)}
						/>
					</div>

					<div className="space-y-2 mt-2">
						<div className="flex items-center gap-3">
							{passwordRequirements.minLength ? (
								<Check className="h-4 w-4 text-green-400 flex-shrink-0" />
							) : (
								<X className="h-4 w-4 text-gray-400 flex-shrink-0" />
							)}
							<span
								className={`text-xs leading-4 ${passwordRequirements.minLength ? 'text-emerald-500' : 'text-gray-400'}`}
							>
								{t('register.passwordRequirements.minLength')}
							</span>
						</div>
						<div className="flex items-center gap-3">
							{passwordRequirements.hasNumber ? (
								<Check className="h-4 w-4 text-green-400 flex-shrink-0" />
							) : (
								<X className="h-4 w-4 text-gray-400 flex-shrink-0" />
							)}
							<span
								className={`text-xs leading-4 ${passwordRequirements.hasNumber ? 'text-emerald-500' : 'text-gray-400'}`}
							>
								{t('register.passwordRequirements.hasNumber')}
							</span>
						</div>
						<div className="flex items-center gap-3">
							{passwordRequirements.hasSpecialChar ? (
								<Check className="h-4 w-4 text-green-400 flex-shrink-0" />
							) : (
								<X className="h-4 w-4 text-gray-400 flex-shrink-0" />
							)}
							<span
								className={`text-xs leading-4 ${passwordRequirements.hasSpecialChar ? 'text-emerald-500' : 'text-gray-400'}`}
							>
								{t('register.passwordRequirements.hasSpecialChar')}
							</span>
						</div>
					</div>

					<div className="mt-8 flex justify-center">
						<Button
							type="submit"
							className="h-10 px-8 py-3 cursor-pointer w-full text-sm font-medium rounded-full"
							disabled={isLoading || !isFormValid}
						>
							{isLoading
								? t('common.loading', { defaultValue: 'Loading...' })
								: t('resetPassword.submit', {
										defaultValue: 'Reset password',
									})}
						</Button>
					</div>
				</form>
			</div>
		</AuthLayout>
	)
}
